/**
 * Per-tenant Brightcove player config, resolved ONCE at site selection (see
 * `cacheBrightcoveConfig`) and cached here so the VOD structured-data builder
 * can stay a synchronous pure function.
 *
 * Only the two PUBLIC ids are cached — `account_id` and `player_id`, both of
 * which already appear in any Brightcove embed URL. The `client_id` /
 * `client_secret` on the same Config Manager record are credentials and are
 * deliberately never read or stored here.
 */
export type BrightcoveConfig = {
  accountId: string;
  playerId: string;
};

const BRIGHTCOVE_CONFIG_KEY = "siteBrightcoveConfig";

/** Brightcove's own default player, used when a tenant configured no player id. */
const DEFAULT_PLAYER_ID = "default";

export const readBrightcoveConfig = (): BrightcoveConfig | undefined => {
  if (typeof localStorage !== "object") return undefined;
  try {
    const stored = localStorage.getItem(BRIGHTCOVE_CONFIG_KEY);
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    return parsed?.accountId
      ? { accountId: parsed.accountId, playerId: parsed.playerId || "" }
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Overwrite the cached config. Fails closed for the same reason the publisher
 * cache does: an account id left over from the previous tenant would build an
 * `embedUrl` pointing at another customer's Brightcove account.
 */
export const writeBrightcoveConfig = (config?: BrightcoveConfig): void => {
  if (typeof localStorage !== "object") return;
  if (!config?.accountId) {
    localStorage.removeItem(BRIGHTCOVE_CONFIG_KEY);
    return;
  }
  localStorage.setItem(BRIGHTCOVE_CONFIG_KEY, JSON.stringify(config));
};

/**
 * The Brightcove iframe URL Google wants for `embedUrl` — a page that plays the
 * video, not the raw media file (that is `contentUrl`).
 *
 * Returns `""` when the video id or the tenant's account id is unknown, so the
 * caller omits `embedUrl` rather than emitting a URL that 404s.
 */
export const buildBrightcoveEmbedUrl = (videoId?: string): string => {
  const config = readBrightcoveConfig();
  if (!videoId || !config?.accountId) return "";
  const playerId = config.playerId || DEFAULT_PLAYER_ID;
  return `https://players.brightcove.net/${config.accountId}/${playerId}_default/index.html?videoId=${videoId}`;
};
